import { Button, DatePicker, Input, Select, Space, TableProps } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import LogViewModalKPI from "../../../../components/OwnKPI/LogViewModalKPI";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../../constants/constants";
import { useGetOwnKpiQuery } from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  GetOwnKpiParams,
  GetOwnKpiResponseType,
} from "../../../../types/performance";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";
import { planTypeColor, statusList } from "../OwnKPI/OwnKPI";

export default function TeamMemberKPI() {
  const { open, handleClose, handleOpen } = useModal<
    { log: any; kpi: string },
    string
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<GetOwnKpiParams>();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { isLoading, isFetching, isError, error, data } = useGetOwnKpiQuery({
    params: {
      ...filter,
      linemanager: authInfo?.id,
      title: keyword,
      positionStatus: "kpi",
      startDate: filter.startDate,
      endDate: filter.startDate,
    },
  });
  useError(isError, error, "Get team KPI fail");

  const columns: TableProps<
    GetOwnKpiResponseType["data"]["kpis"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      width: 200,
      render: (_, record) => record?.kpi?.kpi_title || "n/a",
    },
    {
      title: "Employee ID",
      width: 150,
      render: (_, record) => record?.employeeId || "n/a",
    },
    {
      title: "Employee Name",
      width: 200,
      render: (_, record) => record?.employee?.member?.profile?.name || "n/a",
    },
    {
      title: "Designation",
      width: 150,
      dataIndex: "designation",
      render: (val) => val || "n/a",
    },

    {
      title: "Plan Period",
      width: 200,
      dataIndex: "plan_period",
      render: (_, record) =>
        findPlanVal(planPeriodList, record?.kpi?.plan_period),
    },
    {
      title: "Plan Type",
      width: 150,
      dataIndex: "plan_type",
      render: (_, record) => {
        const type = record?.kpi?.plan_type as keyof typeof planTypeColor;
        return (
          <span
            style={planTypeColor[type] ? { color: planTypeColor[type] } : {}}
          >
            {findPlanVal(planTypeList, type)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 350,
      render: (val) => {
        const st = statusList[val as keyof typeof statusList];
        return <span className={st?.style}>{st?.text}</span>;
      },
    },
    {
      title: "Due Date",
      width: 200,
      render: (_, record) => {
        const match = record?.kpi?.departments?.find(
          (dept: any) => dept.departmentId === record?.departmentId
        );

        return match ? formatDate(match.due_date) : "n/a";
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 200,
      render: (val) => {
        return val ? formatDate(val) : "n/a";
      },
    },

    {
      title: "Action",
      align: "center",
      dataIndex: "id",
      render: (val, record) => (
        <Space>
          <Link to={`/admin/kpi-setup/team-member-kpi/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>

          <Button
            icon={<ActionWatchIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "OWN_KPI_LOG_VIEW",
                data: {
                  log: record?.actionLogs,
                  kpi: record?.kpi?.kpi_title,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <SidebarLayout title="Team Member KPI List">
      {open.type === "OWN_KPI_LOG_VIEW" && (
        <LogViewModalKPI
          open={open.open}
          data={open?.data}
          handleClose={handleClose}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.kpis)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search...`}
              style={{ minWidth: 170, maxWidth: 180, color: "#222" }}
              onChange={handleSearch}
              className="search"
            />

            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("status", val)}
              placeholder="Status"
              filterOption={selectSearchOption}
              options={statusFilterList}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              dropdownStyle={{ minWidth: "230px" }}
            />

            <DatePicker
              format={dateFormat}
              style={{ width: 140 }}
              suffixIcon={<DateIcon />}
              // className="clear-button-hide"
              value={filter?.startDate ? dayjs(filter.startDate) : null}
              onChange={(date) => {
                handleFilter(
                  "startDate",
                  date ? date.format(dbDateFormat) : undefined
                );
              }}
            />
            {/* <Button
              type="primary"
              className="middle-button !p-4"
              icon={<SettingIcon />}
              onClick={() =>
                navigate(`/admin/kpi-setup/team-member-kpi/create`)
              }
            /> */}
          </>
        }
      />
    </SidebarLayout>
  );
}

export const statusFilterList = [
  {
    label: "Pending for Approval",
    value: "pending",
  },
  {
    label: "Approved by Line Manager",
    value: "complete",
  },
  {
    label: "Reject by Line Manager",
    value: "reject",
  },
];
